import React, { useMemo } from 'react';

import { FuturePaymentsList, FuturePaymentListSkeleton } from '../../components/FuturePaymentsList';
import { useConfig } from '../../defaultConfig';
import { useAccounts } from '../../fetchHooks/useAccount';
import { useAllLoanPayments } from '../../fetchHooks/useLoans';
import type { Account } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import * as FuturePaymentService from '../../services/core/FuturePaymentService';
import formatFuturePayments from '../../utils/mapper/futurepaymentsUtil';
import TransactionListStatus from './TransactionListStatus';
import useExtendedFuturepayments from './useExtendedFuturepayments';

type FuturePaymentsInTransactionListProps = {
    account?: Account;
};

const FuturePaymentsInTransactionList: React.FC<FuturePaymentsInTransactionListProps> = ({ account }) => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    const { data: futurePayments, ...futurePaymentsQuery } = useExtendedFuturepayments(account);
    const { data: loanPayments = [] } = useAllLoanPayments(featureToggles.enableFakedLoanFuturePayment);
    const { data: accounts } = useAccounts();

    const runningBalanceStart = featureToggles.enableRunningBalanceForFuturePayments
        ? account?.balance.availableBalance
        : undefined;

    const formattedFuturePayments = useMemo(() => {
        return formatFuturePayments(
            FuturePaymentService.extendWithFakedLoans(futurePayments ?? [], loanPayments, account?.accountNumber),
            runningBalanceStart,
            undefined,
            accounts ?? [],
        );
    }, [futurePayments, loanPayments, account, runningBalanceStart, accounts]);

    const noContent = {
        value: formattedFuturePayments.length === 0,
        title: translated.futurePaymentsList.noHits,
        goToSearch: () => null,
        isFilter: false,
    };

    if (futurePaymentsQuery.isFetching) {
        return <FuturePaymentListSkeleton hideTitle={false} count={futurePayments?.length ?? 5} />;
    }

    if (formattedFuturePayments.length === 0 || futurePaymentsQuery.error) {
        return (
            <TransactionListStatus
                error={futurePaymentsQuery.error}
                refresh={futurePaymentsQuery.refetch}
                noContent={noContent}
                isFuturePayments
            />
        );
    }

    return (
        <FuturePaymentsList
            formattedFuturePayments={formattedFuturePayments}
            availableBalance={account?.balance?.availableBalance}
        />
    );
};

export default FuturePaymentsInTransactionList;
